import type { RadioChangeEvent } from 'antd'
import type {
  IEvent,
  IEventForHistory,
} from 'api/interfaces/events/interfaces.model'
import { Button } from 'components/atoms'
import Breadcrumbs from 'components/atoms/Breadcrumbs'
import { PresentationCard } from 'components/atoms/PresentationCard'
import { TypographyFooter } from 'components/atoms/PresentationCard/PresentationCard.styled'
import type { IEventsBody } from 'home/HomePageContent/Body/Body'
import { SHeader, SLink } from 'home/HomePageContent/Body/Body.styled'
import { InfoBlock } from 'home/HomePageContent/Body/InfoBlock'
import Cookies from 'js-cookie'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { DEFAULT_OBJECTS_PER_PAGE } from 'numberConsts/numberConsts'
import type { FC, ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { pathENUM } from 'registrators/registrators'
import type { RootState } from 'store/store'
import { queryParser } from 'utils/utils'

import { TokenENUM } from '../../../axios/securedAxios'
import { redirectToESIA } from '../../../axios/utils'
import {
  SAdditionDescriptionEvent,
  SButtonEnroll,
  SButtonEnrollWrap,
  SExposition,
  SMainWrapp,
  SPageWrapper,
  SPlug,
  SProgramName,
  SRadio,
  SRadioGroup,
  SSameMero,
  STitlesWrapp,
} from '../../styles/pages.styled'
import { About } from '../components/MenuItems/About/About'
import { Comment } from '../components/MenuItems/Comments/Comment'
import { GalleryEvents } from '../components/MenuItems/Gallery/Gallery'
import { RadioMenuENUM } from '../model/Events.model'

let checkoutRule: string

interface ISelectedEventsProps {
  selectedEvent: IEvent
  recommendedEvents: IEventsBody[]
}

interface IEventFilters {
  age?: number
  date_from?: string
  date_to?: string
  direction_id?: number
  kind?: number
  mo_id?: number
  profile_id?: number
  unit_id?: number
}

export const SelectedEventContent: FC<ISelectedEventsProps> = ({
  selectedEvent,
  recommendedEvents,
}) => {
  const {
    activity_main_info: activityMainInfo,
    limit_size: limitSize,
    start_time: startTime,
    period,
    photos,
  } = selectedEvent
  const router = useRouter()
  const { name, age_pupil: agePupil, addresses } = activityMainInfo

  const { eventsFilters } = useSelector((state: RootState) => state.filters)

  const eventActualHref = eventsFilters?.per_page?.value
    ? `/events?page=1&all_dates=false&only_future_dates=true&sort_by=%5B"date_from"%5D&`
    : `/events?page=1&per_page=${DEFAULT_OBJECTS_PER_PAGE}&all_dates=false&only_future_dates=true&sort_by=%5B"date_from"%5D&`

  const [selectedMenu, setSelectedMenu] = useState<RadioMenuENUM>(
    RadioMenuENUM.about,
  )
  const onRadioChange = useCallback((value: RadioChangeEvent) => {
    setSelectedMenu(value.target.value)
  }, [])

  // @ts-ignore Необходим для обхода типизации фильтров, ошибок не вызывает
  const chosenFiltersByUser: IEventFilters = eventsFilters
    ? queryParser(eventsFilters)
    : null

  const queryChosenByUser = chosenFiltersByUser
    ? `${eventActualHref}${Object.entries(chosenFiltersByUser)
        .join('&')
        .replaceAll(',', '=')}`
    : eventActualHref

  const hrefWithUsedFilters = useMemo(
    () => queryChosenByUser,
    [queryChosenByUser],
  )

  const breadcrumbItems = useMemo(
    () => [
      {
        label: 'Мероприятия',
        href: hrefWithUsedFilters,
      },
      {
        label: name,
        href: '',
      },
    ],
    [hrefWithUsedFilters, name],
  )

  const menuItem: Record<RadioMenuENUM, ReactNode> = useMemo(
    () => ({
      [RadioMenuENUM.about]: <About selectedEvent={selectedEvent} />,
      [RadioMenuENUM.comments]: (
        <Comment eventName={name} id={activityMainInfo.id} />
      ),
      [RadioMenuENUM.gallery]: <GalleryEvents photos={photos} />,
    }),
    [activityMainInfo.id, name, photos, selectedEvent],
  )

  const startTimeEvent = useMemo(() => {
    if (startTime) {
      // Избавляемся от секунд
      return startTime.slice(0, -3)
    }
    return 'Не указано'
  }, [startTime])

  const eventsContent = useMemo(() => {
    if (recommendedEvents.length) {
      return recommendedEvents.map((event: IEventsBody) => (
        <SLink key={event.id} href={`${pathENUM.events}/${event.id}`}>
          <PresentationCard
            key={event.id}
            header={<SHeader>{event.name}</SHeader>}
            footer={
              <TypographyFooter>
                {/* TODO: Use template strings */}
                {event.age_pupil} лет · {event.date_from}
              </TypographyFooter>
            }
          />
        </SLink>
      ))
    }
    return (
      <PresentationCard
        header={<SHeader>Похожих мероприятий еще не появилось :(</SHeader>}
        footer={
          <TypographyFooter>
            Однако совсем скоро они будут здесь!
          </TypographyFooter>
        }
      />
    )
  }, [recommendedEvents])

  const handlePushToRegister = useCallback(() => {
    const cookie = Cookies.get(TokenENUM.accessToken)
    if (!cookie) {
      redirectToESIA()
    } else {
      router.push({
        pathname: `${pathENUM.events}/${activityMainInfo.id}/register`,
      })
    }
  }, [activityMainInfo.id, router])

  const isEventAvailableToRecord = useMemo(() => {
    if (selectedEvent.is_current)
      return (
        <SButtonEnrollWrap>
          <SButtonEnroll>
            <Button onClick={handlePushToRegister}>Записаться</Button>
          </SButtonEnroll>
        </SButtonEnrollWrap>
      )
    return null
  }, [handlePushToRegister, selectedEvent.is_current])

  const addressesList = useMemo(
    () => addresses.map((adr) => adr.address_str),
    [addresses],
  )

  useEffect(() => {
    const currentEvent: IEventForHistory = {
      id: activityMainInfo.id,
      eventName: name,
    }
    const viewedEventsFromLocalStorage =
      window.localStorage.getItem('viewedEvent')
    if (!viewedEventsFromLocalStorage) {
      window.localStorage.setItem('viewedEvent', '[]')
    }
    const existingViewedEvent: IEventForHistory[] | null =
      viewedEventsFromLocalStorage
        ? JSON.parse(viewedEventsFromLocalStorage)
        : null
    if (
      existingViewedEvent &&
      !existingViewedEvent.find(
        (eventInfo: IEventForHistory) => eventInfo.id === activityMainInfo.id,
      )
    ) {
      existingViewedEvent.push(currentEvent)
      localStorage.setItem('viewedEvent', JSON.stringify(existingViewedEvent))
    }
  }, [activityMainInfo.id, name])

  return (
    <SMainWrapp>
      <Head>
        <title>{`Мероприятия | ${name}`}</title>
        <meta name="keywords" content={selectedEvent?.description} />
      </Head>
      <SPageWrapper>
        <Breadcrumbs items={breadcrumbItems} />
        <SExposition>
          <STitlesWrapp>
            <SProgramName>{name}</SProgramName>
            <SAdditionDescriptionEvent>{`Возраст: ${agePupil} лет`}</SAdditionDescriptionEvent>
            <SAdditionDescriptionEvent>
              {`Размер группы: ${limitSize}`} человек
              {checkoutRule}
            </SAdditionDescriptionEvent>
            <SAdditionDescriptionEvent>{`Дата проведения: ${period}`}</SAdditionDescriptionEvent>
            <SAdditionDescriptionEvent>{`Время начала мероприятия: ${startTimeEvent}`}</SAdditionDescriptionEvent>
            <SAdditionDescriptionEvent>{`Адрес: ${addressesList}`}</SAdditionDescriptionEvent>
          </STitlesWrapp>
          {isEventAvailableToRecord}
        </SExposition>
        <SRadioGroup onChange={onRadioChange} value={selectedMenu}>
          <SRadio value={RadioMenuENUM.about}>О мероприятии</SRadio>
          <SRadio value={RadioMenuENUM.comments}>Отзывы</SRadio>
          <SRadio value={RadioMenuENUM.gallery}>Галерея</SRadio>
        </SRadioGroup>
        {menuItem[selectedMenu]}
        <InfoBlock
          title={<SSameMero>Похожие мероприятия</SSameMero>}
          content={<SPlug>{eventsContent}</SPlug>}
        />
      </SPageWrapper>
    </SMainWrapp>
  )
}
